import { useEffect, useLayoutEffect, useState } from 'react';
import { ArrowRight, Rocket, X } from 'lucide-react';

import { STOREFRONT_LAUNCHED_KEY } from '@/components/lifecycle/LaunchStorefrontModal';

/**
 * The "Launch your Local Storefront" focus — a single-target spotlight that
 * follows the intro tour. Where IntroTour walks through the dashboard, this
 * highlights the one key action a new retailer must take, and it deliberately
 * survives the tour being dismissed: it appears once the tour finishes OR is
 * skipped (NEA-5854), so the storefront focus is never lost behind a closed
 * intro.
 *
 * It is intentionally decoupled from the tour:
 *   - IntroTour broadcasts `unify:tour-active` (detail: boolean). We suppress
 *     ourselves while the tour is running, then become eligible when it ends.
 *   - The launch card on the overview carries `data-tour="launch-storefront"`
 *     and only renders while the storefront isn't live, so the element's mere
 *     presence is our "not launched yet" signal — when it's absent (storefront
 *     live, or any other page) we render nothing rather than a dangling overlay.
 *   - Dismissing ("Later" / ✕) writes `unify.launchHighlight.dismissed`; a
 *     (re)started tour clears it so the focus shows again on replay.
 */
const TARGET = '[data-tour="launch-storefront"]';
const DISMISS_KEY = 'unify.launchHighlight.dismissed';
const TOUR_PENDING_KEY = 'unify.tour-pending';
const TOUR_COMPLETED_KEY = 'unify.tour-completed';
const PAD = 10;
const EDGE_MARGIN = 16;
const TIP_WIDTH = 320;

// Rounded-rect subpath for the spotlight hole, wound the same way as the outer
// rect so fill-rule="evenodd" carves it out of the dim layer.
const cutoutPath = ({ left, top, right, bottom, width, height }) => {
	const r = Math.min(18, width / 2, height / 2);
	return [
		`M${left + r} ${top}`,
		`H${right - r}`,
		`A${r} ${r} 0 0 1 ${right} ${top + r}`,
		`V${bottom - r}`,
		`A${r} ${r} 0 0 1 ${right - r} ${bottom}`,
		`H${left + r}`,
		`A${r} ${r} 0 0 1 ${left} ${bottom - r}`,
		`V${top + r}`,
		`A${r} ${r} 0 0 1 ${left + r} ${top}`,
		'Z'
	].join(' ');
};

const read = (key) => {
	try {
		return typeof window !== 'undefined' && window.localStorage.getItem(key) === '1';
	} catch {
		return false;
	}
};

export function LaunchHighlight() {
	const [rect, setRect] = useState(null);
	const [targetFound, setTargetFound] = useState(false);
	const [visible, setVisible] = useState(false);
	const [launched, setLaunched] = useState(() => read(STOREFRONT_LAUNCHED_KEY));
	const [dismissed, setDismissed] = useState(() => read(DISMISS_KEY));
	// Mirror IntroTour's auto-start condition so we don't flash before the tour
	// takes over on first visit. The `unify:tour-active` event keeps this honest
	// afterwards (and for tour replays).
	const [tourRunning, setTourRunning] = useState(() => read(TOUR_PENDING_KEY) && !read(TOUR_COMPLETED_KEY));

	useEffect(() => {
		const syncLaunched = () => setLaunched(read(STOREFRONT_LAUNCHED_KEY));
		const onTour = (e) => {
			const running = !!e.detail;
			setTourRunning(running);
			// A (re)started tour re-arms the focus: clear any previous dismissal so
			// it shows again once the tour finishes or is skipped.
			if (running) {
				try {
					window.localStorage.removeItem(DISMISS_KEY);
				} catch {
					/* ignore */
				}
				setDismissed(false);
			}
		};
		window.addEventListener('storefront:launched', syncLaunched);
		window.addEventListener('storage', syncLaunched);
		window.addEventListener('unify:tour-active', onTour);
		return () => {
			window.removeEventListener('storefront:launched', syncLaunched);
			window.removeEventListener('storage', syncLaunched);
			window.removeEventListener('unify:tour-active', onTour);
		};
	}, []);

	const eligible = !launched && !dismissed && !tourRunning;

	// Locate the launch card. It can mount after billing/channels load, so poll
	// until found (then stop — resize/scroll listeners keep the rect fresh).
	useLayoutEffect(() => {
		if (!eligible) {
			setTargetFound(false);
			setRect(null);
			return undefined;
		}
		const measure = () => {
			const el = document.querySelector(TARGET);
			setTargetFound(!!el);
			setRect(el ? el.getBoundingClientRect() : null);
			return !!el;
		};
		const found = measure();
		const interval = found ? null : setInterval(() => measure() && clearInterval(interval), 200);
		const giveUp = setTimeout(() => interval && clearInterval(interval), 8000);
		window.addEventListener('resize', measure);
		window.addEventListener('scroll', measure, true);
		return () => {
			if (interval) clearInterval(interval);
			clearTimeout(giveUp);
			window.removeEventListener('resize', measure);
			window.removeEventListener('scroll', measure, true);
		};
	}, [eligible]);

	const active = eligible && targetFound;

	useEffect(() => {
		if (!active) {
			setVisible(false);
			return undefined;
		}
		document.querySelector(TARGET)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		const t = setTimeout(() => setVisible(true), 400);
		return () => clearTimeout(t);
	}, [active]);

	const dismiss = () => {
		try {
			window.localStorage.setItem(DISMISS_KEY, '1');
		} catch {
			/* ignore */
		}
		setDismissed(true);
	};

	// The spotlight cutout lets clicks reach the real launch card (see the svg
	// below) — clear the focus as that first click goes through, so it doesn't
	// linger behind the launch modal.
	useEffect(() => {
		if (!active) return undefined;
		const el = document.querySelector(TARGET);
		if (!el) return undefined;
		el.addEventListener('click', dismiss);
		return () => el.removeEventListener('click', dismiss);
	}, [active]);

	if (!active) return null;

	const spot = rect
		? (() => {
				const x1 = Math.max(EDGE_MARGIN, rect.left - PAD);
				const y1 = Math.max(EDGE_MARGIN, rect.top - PAD);
				const x2 = Math.min(window.innerWidth - EDGE_MARGIN, rect.right + PAD);
				const y2 = Math.min(window.innerHeight - EDGE_MARGIN, rect.bottom + PAD);
				return { left: x1, top: y1, width: Math.max(0, x2 - x1), height: Math.max(0, y2 - y1), right: x2, bottom: y2 };
			})()
		: null;

	const launchNow = () => {
		document.querySelector(TARGET)?.click();
		dismiss();
	};

	// Prefer the tooltip to the left of the card; fall back to below it.
	const EDGE = 24;
	let tipStyle = { top: EDGE, left: EDGE };
	if (spot) {
		const clampTop = (t) => Math.max(EDGE, Math.min(window.innerHeight - 240, t));
		const clampLeft = (l) => Math.max(EDGE, Math.min(window.innerWidth - TIP_WIDTH - EDGE, l));
		if (spot.left >= TIP_WIDTH + EDGE + 16) {
			tipStyle = { top: clampTop(spot.top), left: clampLeft(spot.left - TIP_WIDTH - 16) };
		} else {
			tipStyle = { top: clampTop(spot.bottom + 16), left: clampLeft(spot.left) };
		}
	}

	return (
		<div
			className={`pointer-events-none fixed inset-0 z-[60] transition-opacity duration-300 ease-out ${visible ? 'opacity-100' : 'opacity-0'}`}
		>
			{/* The dim layer is one evenodd path so the spotlight cutout is a true
			    hole: SVG hit-testing follows the painted fill, so a click on the
			    highlighted card goes straight through to it on the first click
			    (NEA-5937) — a masked full-screen rect would swallow it. */}
			<svg className="pointer-events-none absolute inset-0 h-full w-full">
				<path
					className="pointer-events-auto"
					d={`M0 0 H100000 V100000 H0 Z ${spot ? cutoutPath(spot) : ''}`}
					fill="hsl(var(--foreground) / 0.55)"
					fillRule="evenodd"
					onClick={dismiss}
				/>
			</svg>

			{spot ? (
				<div
					className="pointer-events-none absolute animate-pulse rounded-[18px] ring-2 ring-primary shadow-[0_0_0_6px_hsl(var(--primary)/0.25)] transition-all duration-200"
					style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height }}
				/>
			) : null}

			<div
				className="pointer-events-auto absolute w-[320px] rounded-2xl bg-gradient-unify p-5 text-white shadow-2xl ring-1 ring-white/15"
				style={tipStyle}
			>
				<div className="mb-2 flex items-center justify-between">
					<span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-white">
						<Rocket className="h-3 w-3" /> Start here
					</span>
					<button type="button" onClick={dismiss} aria-label="Dismiss" className="text-white/60 transition hover:text-white">
						<X className="h-4 w-4" />
					</button>
				</div>
				<h3 className="text-base font-extrabold tracking-tight text-white">Launch your Local Storefront!</h3>
				<p className="mt-1.5 text-sm text-white/80">
					First things first — click <span className="font-semibold text-white">Launch Local Storefront</span> to bring your
					in-store products to life across search engines and AI.
				</p>
				<div className="mt-5 flex items-center justify-end gap-2">
					<button
						type="button"
						onClick={dismiss}
						className="rounded-full px-3 py-1.5 text-xs font-semibold text-white/80 transition hover:bg-white/10"
					>
						Later
					</button>
					<button
						type="button"
						onClick={launchNow}
						className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-1.5 text-xs font-semibold text-primary transition hover:bg-white/90"
					>
						Launch now <ArrowRight className="h-3.5 w-3.5" />
					</button>
				</div>
			</div>
		</div>
	);
}
